import React, { useCallback, useEffect } from 'react';
import { type ResizableElement } from '../../../types/aha-editor';

const useDetectElementSize = (element: ResizableElement) => {
  const [width, setWidth] = React.useState(element.resizer_width);
  const [height, setHeight] = React.useState(element.resizer_height);
  const [originalWidth, setOriginalWidth] = React.useState(
    element.resizer_originalWidth || element.resizer_width
  );
  const [originalHeight, setOriginalHeight] = React.useState(
    element.resizer_originalHeight || element.resizer_height
  );

  useEffect(() => {
    if (element.resizer_width && element.resizer_width !== width) {
      setWidth(element.resizer_width);
    }
    if (element.resizer_height && element.resizer_height !== height) {
      setHeight(element.resizer_height);
    }
  }, [element]);

  const setDetectedElementSize = useCallback(
    (node: HTMLImageElement | HTMLVideoElement) => {
      let naturalWidth: number;
      let naturalHeight: number;

      if (node instanceof HTMLVideoElement) {
        naturalWidth = node.videoWidth;
        naturalHeight = node.videoHeight;
      } else {
        naturalWidth = node.naturalWidth;
        naturalHeight = node.naturalHeight;
      }

      if (naturalHeight !== originalHeight) {
        setOriginalHeight(naturalHeight);
      }

      if (naturalWidth !== originalWidth) {
        setOriginalWidth(naturalWidth);
      }

      if (width && height) return;

      if (!height && width) {
        setHeight((naturalHeight * width) / naturalWidth);
      } else if (!width && height) {
        setWidth((naturalWidth * height) / naturalHeight);
      } else {
        setWidth(naturalWidth);
        setHeight(naturalHeight);
      }
    },
    []
  );

  const detectElementSize = useCallback(
    (node: HTMLImageElement | HTMLVideoElement | null) => {
      if (!node) return;

      if (node instanceof HTMLVideoElement) {
        node.addEventListener('loadedmetadata', () =>
          setDetectedElementSize(node)
        );
      }

      if (node instanceof HTMLImageElement) {
        if (node.complete) {
          setDetectedElementSize(node);
        } else {
          node.addEventListener('load', () => setDetectedElementSize(node));
        }
      }
    },
    [width, height]
  );

  return { detectElementSize, width, height, originalWidth, originalHeight };
};

export default useDetectElementSize;
